import { useCallback, useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as characterService from '@/services/characterService';
import { rarityMeta, hexToRgba, initials, ACCENT_NEON } from '@/utils/characters';

const MAX_STAGE = 4;

const moodEmoji = {
  neutral: '😐', happy: '😊', suspicious: '🤨', excited: '🤩', sad: '😔', cryptic: '🌙',
};

function Section({ title, children, brand }) {
  return (
    <section className="mt-5">
      <h4 className="mb-2 font-urban text-xs font-bold uppercase tracking-widest" style={{ color: brand }}>
        {title}
      </h4>
      {children}
    </section>
  );
}

export default function CharacterDetailModal({ characterKey, isOpen, onClose, onRelationshipChange }) {
  const [character, setCharacter] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const loadDetail = useCallback(async () => {
    const res = await characterService.getCharacter(characterKey);
    const data = res.data.data;
    setCharacter(data);
    setRelationship(data.relationship || { met: false, stage: 0, points: 0, progress: { pct: 0, next: null } });
    return data;
  }, [characterKey]);

  // On open: register the meeting, then load detail at the player's real stage.
  useEffect(() => {
    if (!isOpen || !characterKey) return;
    let cancelled = false;
    setCharacter(null);
    setRelationship(null);
    setFlash(null);
    setStatus('A carregar...');

    (async () => {
      try {
        await characterService.meetCharacter(characterKey).catch(() => {}); // best-effort
        if (cancelled) return;
        await loadDetail();
        if (cancelled) return;
        setStatus('');
        onRelationshipChange?.();
      } catch (err) {
        if (cancelled) return;
        setStatus(err.response?.data?.message || 'Não foi possível carregar esta personagem.');
      }
    })();

    return () => { cancelled = true; };
  }, [characterKey, isOpen, loadDetail, onRelationshipChange]);

  const handleInteract = async () => {
    if (busy) return;
    setBusy(true);
    setFlash(null);
    try {
      const res = await characterService.interactCharacter(characterKey, 'talk');
      const result = res.data.data;
      setRelationship(result.relationship);
      if (result.leveledUp) {
        setFlash({
          message: `Proximidade nível ${result.relationship.stage}!`,
          secrets: result.newlyUnlockedSecrets || [],
        });
        await loadDetail(); // reveal newly unlocked secrets & dialogues
      }
      onRelationshipChange?.();
    } catch (err) {
      setStatus(err.response?.data?.message || 'Não foi possível interagir agora.');
    } finally {
      setBusy(false);
    }
  };

  const brand = character?.brandColor || ACCENT_NEON;
  const rarity = rarityMeta(character?.rarity);
  const personality = character?.personality || {};
  const stage = relationship?.stage ?? 0;
  const pct = relationship?.progress?.pct ?? 0;
  const nextAt = relationship?.progress?.next;
  const maxed = stage >= MAX_STAGE;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div
        className="relative overflow-hidden rounded-3xl border-2 bg-background"
        style={{
          borderColor: hexToRgba(brand, 0.5),
          boxShadow: `0 0 40px ${hexToRgba(brand, 0.35)}`,
        }}
      >
        {/* header */}
        <div
          className="relative px-5 pb-4 pt-6"
          style={{ background: `linear-gradient(160deg, ${hexToRgba(brand, 0.35)} 0%, transparent 80%)` }}
        >
          <button
            onClick={onClose}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-edge bg-surface text-fg transition hover:text-accent"
            aria-label="Fechar"
          >
            ✕
          </button>

          <div className="flex items-center gap-4">
            <div
              className="city-pulse flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 font-urban text-3xl font-extrabold text-white"
              style={{ '--pulse-a': brand, '--pulse-b': ACCENT_NEON, background: hexToRgba(brand, 0.25) }}
            >
              {character?.avatarUrl ? (
                <img src={character.avatarUrl} alt={character.name} className="h-full w-full object-cover" />
              ) : (
                initials(character?.name || '')
              )}
            </div>
            <div className="min-w-0 flex-1">
              <span
                className="mb-1 inline-block rounded-full px-2 py-0.5 font-micro text-[10px] font-bold uppercase tracking-wider"
                style={{ color: rarity.color, background: hexToRgba(rarity.color, 0.14), border: `1px solid ${hexToRgba(rarity.color, 0.4)}` }}
              >
                {rarity.label}
              </span>
              <h2 className="font-urban text-2xl font-extrabold leading-tight text-fg">{character?.name}</h2>
              <p className="text-sm text-fg/70">{character?.occupation}</p>
              <p className="font-micro text-[11px] text-muted">
                {character?.neighborhood}{character?.age ? ` · ${character.age} anos` : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[62vh] overflow-y-auto px-5 pb-6">
          {status && <p className="py-6 text-center text-sm text-muted">{status}</p>}

          {character && (
            <>
              {/* relationship progression */}
              <div className="mt-3 rounded-xl border border-edge bg-surfaceAlt px-3 py-3">
                <div className="mb-2 flex items-center justify-between font-micro text-[11px] text-muted">
                  <span>Proximidade · nível <span className="font-bold" style={{ color: brand }}>{stage}</span> / {MAX_STAGE}</span>
                  <span className="opacity-80">
                    {maxed ? 'máximo' : `${relationship?.points ?? 0} / ${nextAt} pts`}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${maxed ? 100 : pct}%`, background: `linear-gradient(90deg, ${brand}, ${ACCENT_NEON})` }}
                  />
                </div>
                <button
                  onClick={handleInteract}
                  disabled={busy || maxed}
                  className="mt-3 w-full rounded-xl border-2 py-2 font-urban text-sm font-bold text-fg transition active:scale-[0.98] disabled:opacity-50"
                  style={{ borderColor: hexToRgba(brand, 0.6), background: hexToRgba(brand, 0.18) }}
                >
                  {maxed ? 'Relação no máximo' : busy ? 'A conversar...' : 'Aproximar-se ✦'}
                </button>

                {flash && (
                  <div
                    className="mt-3 rounded-xl border px-3 py-2"
                    style={{ borderColor: hexToRgba(brand, 0.5), background: hexToRgba(brand, 0.12) }}
                  >
                    <p className="font-urban text-sm font-bold" style={{ color: brand }}>✦ {flash.message}</p>
                    {flash.secrets.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {flash.secrets.map((s, i) => (
                          <li key={i} className="text-[12px] text-fg/80">🔓 Novo segredo: <span className="font-bold">{s.title}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* bio */}
              {character.bio && (
                <Section title="Biografia" brand={brand}>
                  {character.bio.split('\n').filter(Boolean).map((p, i) => (
                    <p key={i} className="mb-2 text-[13px] leading-relaxed text-fg/75">{p}</p>
                  ))}
                </Section>
              )}

              {/* personality */}
              {(personality.traits?.length || personality.speechStyle) && (
                <Section title="Personalidade" brand={brand}>
                  <div className="flex flex-wrap gap-1.5">
                    {(personality.traits || []).map((t) => (
                      <span key={t} className="rounded-full px-2 py-0.5 text-[11px] text-fg" style={{ background: hexToRgba(brand, 0.14) }}>
                        {t}
                      </span>
                    ))}
                  </div>
                  {personality.speechStyle && (
                    <p className="mt-2 text-[13px] italic leading-relaxed text-fg/65">“{personality.speechStyle}”</p>
                  )}
                </Section>
              )}

              {/* motivation */}
              {character.motivation && (
                <Section title="Motivação" brand={brand}>
                  <p className="text-[13px] leading-relaxed text-fg/75">{character.motivation}</p>
                </Section>
              )}

              {/* secrets */}
              {character.secrets?.length > 0 && (
                <Section title="Segredos" brand={brand}>
                  <div className="space-y-2">
                    {character.secrets.map((s, i) => (
                      <div
                        key={i}
                        className="rounded-xl border px-3 py-2"
                        style={{
                          borderColor: s.unlocked ? hexToRgba(brand, 0.4) : 'rgb(var(--hc-border))',
                          background: s.unlocked ? hexToRgba(brand, 0.08) : 'rgb(var(--hc-surface-alt))',
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-urban text-[13px] font-bold text-fg">
                            {s.unlocked ? s.title : '🔒 Segredo bloqueado'}
                          </span>
                          <span className="font-micro text-[10px] text-muted">Nível {s.stage}</span>
                        </div>
                        {s.unlocked ? (
                          <p className="mt-1 text-[12px] leading-relaxed text-fg/70">{s.text}</p>
                        ) : (
                          <p className="mt-1 text-[12px] italic text-muted">Aproxima-te mais para desvendar.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* dialogues */}
              {character.dialogues?.length > 0 && (
                <Section title="Diálogos" brand={brand}>
                  <div className="space-y-2">
                    {character.dialogues.map((d) => (
                      <div key={d.id} className="rounded-xl border border-edge bg-surface px-3 py-2">
                        <div className="mb-1 flex items-center gap-2 font-micro text-[10px] uppercase tracking-wider text-muted">
                          <span>{moodEmoji[d.mood] || ''}</span>
                          <span>{d.triggerType?.replace('_', ' ')}</span>
                          {d.triggerValue ? <span className="opacity-70">· {d.triggerValue}</span> : null}
                        </div>
                        <p className="text-[13px] leading-relaxed text-fg/80">{d.dialogue}</p>
                        {Array.isArray(d.choices) && d.choices.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {d.choices.map((c) => (
                              <div key={c.id} className="rounded-lg border border-edge px-2 py-1 text-[12px] text-fg/60">
                                › {c.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* missions */}
              {character.missions?.length > 0 && (
                <Section title="Missões" brand={brand}>
                  <div className="space-y-2">
                    {character.missions.map((m) => (
                      <div key={m.id} className="rounded-xl border border-edge bg-surface px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="font-urban text-[13px] font-bold text-fg">{m.title}</span>
                          <span className="font-micro text-[10px] uppercase" style={{ color: brand }}>{m.difficulty}</span>
                        </div>
                        <p className="mt-1 text-[12px] leading-relaxed text-fg/65">{m.description}</p>
                        <div className="mt-1.5 font-micro text-[10px] text-muted">
                          {m.reward?.coins ? `🪙 ${m.reward.coins}` : ''} {m.reward?.xp ? `· ⭐ ${m.reward.xp} XP` : ''} {m.estimatedMinutes ? `· ~${m.estimatedMinutes} min` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* known locations */}
              {character.knownLocations?.length > 0 && (
                <Section title="Onde encontrá-la(o)" brand={brand}>
                  <div className="flex flex-wrap gap-1.5">
                    {character.knownLocations.map((l) => (
                      <span key={l} className="rounded-lg border border-edge px-2 py-0.5 font-micro text-[11px] text-muted">📍 {l}</span>
                    ))}
                  </div>
                </Section>
              )}

              {/* relationships */}
              {character.relationships?.length > 0 && (
                <Section title="Relações" brand={brand}>
                  <div className="space-y-1.5">
                    {character.relationships.map((r) => (
                      <div key={r.slug} className="text-[12px] text-fg/70">
                        <span className="font-bold text-fg">{r.name}</span>
                        <span className="text-muted"> — {r.relation}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
